import { Check, X } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

export function ComparisonSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const prefersReducedMotion = useReducedMotion();

  const comparisons = [
    { traditional: "Days of manual writing and formatting", scholar42: "Get structured drafts in minutes" },
    { traditional: "Manual citation management and errors", scholar42: "Automated, accurate citations" },
    { traditional: "Struggling with academic writing structure", scholar42: "AI-guided organization and flow" },
    { traditional: "Constant formatting headaches", scholar42: "Professional formatting built-in" },
    { traditional: "Isolated writing process", scholar42: "Integrated research workflow" }
  ];

  return (
    <section ref={ref} className="relative py-20 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-white dark:from-slate-900 dark:via-slate-950 dark:to-slate-900"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[--text-primary] mb-4">
            Traditional vs Scholar42
          </h2>
          <p className="text-lg sm:text-xl text-[--text-secondary] max-w-3xl mx-auto">
            See how Scholar42 transforms your academic writing workflow
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto">
          <div className="hidden md:grid md:grid-cols-2 gap-6 mb-6">
            <motion.div
              className="text-center p-4 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-t-2xl border-t-4 border-red-500"
              initial={prefersReducedMotion ? false : { opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3 className="text-xl font-bold text-red-700 dark:text-red-400 flex items-center justify-center gap-2">
                <X className="w-6 h-6" />
                Traditional Way
              </h3>
            </motion.div>
            <motion.div
              className="text-center p-4 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-t-2xl border-t-4 border-emerald-500"
              initial={prefersReducedMotion ? false : { opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3 className="text-xl font-bold text-emerald-700 dark:text-emerald-400 flex items-center justify-center gap-2">
                <Check className="w-6 h-6" />
                With Scholar42
              </h3>
            </motion.div>
          </div>

          <div className="space-y-4">
            {comparisons.map((comparison, index) => (
              <motion.div
                key={index}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 0.6,
                  delay: index * 0.08,
                  ease: [0.22, 1, 0.36, 1]
                }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-slate-800 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden border border-slate-200 dark:border-slate-700"
              >
                <div className="p-6 bg-gradient-to-br from-red-50/50 to-transparent dark:from-red-900/10 dark:to-transparent border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-700">
                  <div className="md:hidden mb-2 flex items-center gap-2 text-red-600 dark:text-red-400 font-semibold">
                    <X className="w-5 h-5" />
                    Traditional
                  </div>
                  <p className="text-[--text-secondary] flex items-start gap-2">
                    <X className="hidden md:block w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>{comparison.traditional}</span>
                  </p>
                </div>
                <div className="p-6 bg-gradient-to-br from-emerald-50/30 to-transparent dark:from-emerald-900/10 dark:to-transparent">
                  <div className="md:hidden mb-2 flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-semibold">
                    <Check className="w-5 h-5" />
                    Scholar42
                  </div>
                  <p className="text-[--text-primary] font-medium flex items-start gap-2">
                    <Check className="hidden md:block w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>{comparison.scholar42}</span>
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}